import { graphql } from "gatsby";
import React from "react";

import BlockList from "~/blocks/BlockList";

import Primary from "~/components/layout/Primary";

import { getPageBreadcrumbs } from "~/utilities/breadcrumbs";
import Breadcrumbs from "~/components/Breadcrumbs";
import CompareTool from "~/components/CompareTool";
import Seo from "~/components/Seo";

import { useCompareContext } from "~/context/CompareContext";
import useAllBlocksLoaded from "~/hooks/useAllBlocksLoaded";

import { createSeoSchemas } from "~/utilities/format";

import withTranslationProps from "~/hoc/withTranslationProps";
import { translatePageBreadcrumbs } from "~/translators/functions";

const Page = ({ data, location, translationProps, pageContext }) => {
    const {
        blocks,
        title,
        translations,
        customAttributes: {
            hideBreadcrumbs,
            noMarginBottomForBreadcrumbs,
            breadcrumbBackgroundColor,
            stickyBreadcrumbs,
        },
        ancestors,
    } = data.wpPage;

    const { selectedProducts } = useCompareContext();
    const { allBlocksAreLoaded, setBlockAsLoaded } = useAllBlocksLoaded(
        blocks.length,
    );

    const nodes = translatePageBreadcrumbs(
        translations,
        translationProps,
        ancestors?.nodes,
    );

    const breadcrumbs = getPageBreadcrumbs(nodes, title);

    return (
        <Primary
            {...{ allBlocksAreLoaded }}
            headerNotSticky={stickyBreadcrumbs}
            pageTitle={title}
            pageContext={pageContext}
        >
            <Breadcrumbs
                {...{ hideBreadcrumbs }}
                crumbs={breadcrumbs}
                noMarginBottom={noMarginBottomForBreadcrumbs}
                backgroundColor={breadcrumbBackgroundColor}
                sticky={stickyBreadcrumbs}
            />

            <BlockList {...{ blocks, setBlockAsLoaded }} />

            <CompareTool {...{ selectedProducts, location }} />
        </Primary>
    );
};

export const Head = ({ data, location }) => {
    const { title, seo, ancestors, blocks } = data.wpPage;
    const rawSchema = { schemas: blocks };
    const schemas = createSeoSchemas(rawSchema);

    const breadcrumbs = getPageBreadcrumbs(ancestors?.nodes, title);

    return (
        <Seo
            {...{ seo, breadcrumbs, location, seoSchema: schemas }}
            pageTitle={title}
        />
    );
};

export const query = graphql`
    query PageQuery($id: String!) {
        wpPage(id: { eq: $id }) {
            id
            slug
            title
            translations {
                title
                languageCode
                ancestors {
                    nodes {
                        ... on WpPage {
                            id
                            title
                            uri
                            languageCode
                            slug
                            translations {
                                id
                                title
                                uri
                                languageCode
                                slug
                            }
                        }
                    }
                }
            }
            seo {
                ...SeoPostFragment
            }
            customAttributes {
                hideBreadcrumbs
                noMarginBottomForBreadcrumbs
                breadcrumbBackgroundColor
                stickyBreadcrumbs
            }
            ancestors {
                nodes {
                    slug
                    ... on WpPage {
                        id
                        title
                        uri
                    }
                }
            }
            blocks {
                ...AcfQuoteBlockFragment
                ...AccordionsBlockFragment
                ...AdBlockFragment
                ...AdCardsGroupBlockFragment
                ...AllApplicationsBlockFragment
                ...AllCaseStudiesBlockFragment
                ...AllEventsBlockFragment
                ...AllFaqsBlockFragment
                ...AllNewsBlockFragment
                ...AllInsightsBlockFragment
                ...ButtonsBlockFragment
                ...CalculatorBlockFragment
                ...CardLinksBlockFragment
                ...CodeBlockFragment
                ...ColumnsBlockFragment
                ...ContactBlockFragment
                ...DocumentsFragment
                ...FaqsBlockFragment
                ...DocumentLibraryBlockFragment
                ...EmbedBlockFragment
                ...FeaturesBlockFragment
                ...FileBlockFragment
                ...FilterAdBlockFragment
                ...FreeformBlockFragment
                ...GravityFormBlockFragment
                ...GroupBlockFragment
                ...HeadingBlockFragment
                ...HeroBlockFragment
                ...HeroCardsBlockFragment
                ...HeroLogoButtonsBlockFragment
                ...HtmlBlockFragment
                ...HubspotFormBlockFragment
                ...IconLinksBlockFragment
                ...IframeEmbedBlockFragment
                ...ImageBlockFragment
                ...ImageCardsBlockFragment
                ...ImageTextBlockFragment
                ...InformationBlockFragment
                ...InsightsBlockFragment
                ...JsEmbedBlockFragment
                ...JumpLinksNavigationBlockFragment
                ...LinksAdBlockFragment
                ...ListBlockFragment
                ...LogosBlockFragment
                ...MediaTextBlockFragment
                ...PageHeaderBlockFragment
                ...ParagraphBlockFragment
                ...ProductsCarouselFragment
                ...ProductSupportFragment
                ...PullquoteBlockFragment
                ...QrScannerBlockFragment
                ...QuoteBlockFragment
                ...QuoteSliderBlockFragment
                ...RmaFormBlockFragment
                ...SearchResultsBlockFragment
                ...SeparatorBlockFragment
                ...SiteSearchBlockFragment
                ...SpacerBlockFragment
                ...TableBlockFragment
                ...TimelineBlockFragment
                ...VideoEmbedBlockFragment
                ...VideoLinksBlockFragment
                ...WhereToBuyBlockFragment
            }
        }
    }
`;

export default withTranslationProps(Page);
